import React from "react";
import ReactDOM from "react-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import {
  fetchTotalTradeSum,
  fetchPortfolio,
  fetchPortfolioCharts,
} from "../api";
import { useNotifications } from "../helpers/useNotifications";
import Table from "../components/Table/Table";
import formatNumber from "../helpers/formatNumber";
import Grid from "../components/Grid/Grid";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Spinner from "../components/Spinner/Spinner";
import Charts from "../components/Charts/Charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartSimple,
  faCoins,
  faHandHoldingDollar,
  faMoneyBillTrendUp,
  faTents,
} from "@fortawesome/free-solid-svg-icons";
import { faInstalod, faNeos } from "@fortawesome/free-brands-svg-icons";

function Portfolio({ title }) {
  const createNotification = useNotifications();
  const { currentUser } = useAuth(); // fix: useAuth aufrufen
  const queryClient = useQueryClient();
  const limit = 25;

  //* load portfolio investment sum
  const {
    data: dbTotalTrades,
    isError,
    error,
  } = useQuery(["myTotalTrades"], fetchTotalTradeSum);

  //* load portfolio charts
  const { data: dbPortfolioCharts } = useQuery(
    ["myPortfolioCharts"],
    fetchPortfolioCharts,
  );

  //* load portfolio by limit
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(
      ["myportfolio"],
      async ({ pageParam = 0 }) => fetchPortfolio(pageParam, limit),
      {
        getNextPageParam: (lastPage, allPages) =>
          lastPage.length < limit ? undefined : allPages.length,
        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: currentUser?.authenticated,
      },
    );

  const allItems = data?.pages.flat() || [];

  if (isLoading) return <Spinner />;
  if (isError) createNotification("error", error.message);

  const columns = [
    {
      label: "",
      field: "ticker",
      colWidth: "75%",
      rowValue: (_, row) => (
        <div className="dividend-payment-container">
          <div className="dividend-payment-image-container">
            <Link
              to={
                row?.company?.category === "nft"
                  ? "/portfolio/nft"
                  : row?.company?.category === "property"
                    ? "/portfolio/property"
                    : `/blog/${row?.company?.category || ""}/${row.ticker}`
              }
            >
              {row?.company?.category === "nft" ||
              row?.company?.category === "property" ? (
                <FontAwesomeIcon
                  className="text-light table-thumbnail"
                  size="lg"
                  height={45}
                  width={45}
                  style={{ marginRight: 8 }}
                  icon={row?.company?.category === "nft" ? faNeos : faTents}
                />
              ) : (
                <img
                  src={
                    row.company?.logo ||
                    `https://assets.parqet.com/logos/symbol/${row.ticker}`
                  }
                  alt={row.ticker}
                  className="table-thumbnail"
                  height={45}
                  width={45}
                  style={{ marginRight: 8 }}
                />
              )}
            </Link>
          </div>
          <div className="dividend-payment-text-container">
            <div className="dividend-payment-date text-muted">
              <span className="trades-share-badge badge badge-info">
                {row?.company?.category === "assets"
                  ? formatNumber(1)
                  : formatNumber(row?.totalShare)}
              </span>
            </div>
            <div className="dividend-payment-name text-light">
              {row.company?.name || row?.ticker}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "",
      field: "totalInvestment",
      colWidth: "25%",
      rowValue: (val, row) => {
        const currentValue =
          row?.company?.category === "nft"
            ? row?.company?.price
            : row?.company?.price
              ? row?.totalShare * row?.company?.price
              : val;
        return (
          <div className="d-flex align-items-center p-3 justify-content-end">
            <span className="dividend-payment-value  badge badge-success">
              + {formatNumber(currentValue)} €
            </span>
          </div>
        );
      },
    },
  ];

  const chartData = dbPortfolioCharts?.allocation?.map((stock) => ({
    name: stock.company?.name,
    value: stock.currentValue || 0,
    absolute: stock.currentValue || 0,
  }));

  const performance =
    (allItems?.[0]?.portfolioValue / dbPortfolioCharts?.keyfacts?.invested -
      1) *
    100;

  const profit =
    allItems?.[0]?.portfolioValue -
    dbPortfolioCharts?.keyfacts?.invested +
    dbPortfolioCharts?.keyfacts?.payments;

  return (
    <>
      <Charts.Pie
        showActiveKey
        dataset={chartData}
        colors={[
          "#1E7BC6",
          "#7266AE",
          "#A59CCE",
          "#D87FA0",
          "#E46682",
          "#E47A4C",
          "#F2B42B",
          "#4098A2",
          "#2E9EFF",
          "#7B8FA1",
        ]}
        startPosition={180}
        endPosition={0}
        innerRadius="75%"
        animated={true}
        label={`${formatNumber(allItems?.[0]?.portfolioValue)} €`}
        metaLabel="Portfolio"
        className="pie-chart-portfolio-container"
      />

      <br />
      <div>
        <Grid>
          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon icon={faCoins} className="text-muted" />
              <p className="stockinfo-keyfacts-value text-lawngreen">
                {`${formatNumber(
                  dbPortfolioCharts?.keyfacts?.invested.toFixed(2),
                )}`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">
                Investiert €
              </p>
            </div>
          </div>

          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon icon={faChartSimple} className="text-muted" />
              <p
                className="stockinfo-keyfacts-value"
                style={{ color: performance < 0 ? "tomato" : "lawngreen" }}
              >
                {`${formatNumber(performance.toFixed(2))} %`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">Performance</p>
            </div>
          </div>

          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon
                icon={faHandHoldingDollar}
                className="text-muted"
              />
              <p className="stockinfo-keyfacts-value text-lawngreen">
                {`${formatNumber(
                  dbPortfolioCharts?.keyfacts?.payments.toFixed(2),
                )}`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">Erträge €</p>
            </div>
          </div>

          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon
                icon={faMoneyBillTrendUp}
                className="text-muted"
              />
              <p
                className="stockinfo-keyfacts-value"
                style={{ color: profit < 0 ? "tomato" : "lawngreen" }}
              >
                {`${formatNumber(profit.toFixed(2))}`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">Rendite €</p>
            </div>
          </div>
        </Grid>
      </div>

      <div className="article-main">
        <Grid>
          <h5 className="text-light text-center">
            Portfolio
            <button
              className="btn btn-link dismiss-active-or-focus delete-form-label"
              onClick={(e) => {
                e.preventDefault();
                queryClient.invalidateQueries([
                  "myPortfolioCharts",
                  "myportfolio",
                  "myTotalTrades",
                ]);
              }}
            >
              <FontAwesomeIcon icon={faInstalod} />
            </button>
          </h5>
          <Table data={allItems} columns={columns} />
        </Grid>
      </div>
      {hasNextPage && (
        <div className="text-center mt-6">
          <LoadingButton
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          />
        </div>
      )}
    </>
  );
}

export default Portfolio;
